import { fg } from '@confluence/feature-gating';
import { getRequest, type ReaderSelection } from 'relay-runtime';
import { type OperationVariables } from '@apollo/react-common';
import { type MutationOptions } from 'apollo-client';
import { type GraphQLTaggedNode } from 'react-relay';
import { type FetchResult } from 'apollo-link';

import { writeQueryPayloadToStore } from './relayStorePayloadWrite.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { commitMutation } from './commitMutation.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { apolloCompatibleMutation } from './apolloCompatibleMutation.ts';
import { convertToApolloError } from './query.ts';
// Package-local wrapper — surfaces refetch errors as `ApolloErrorInterop`.
import { fetchQuery } from './fetchQuery.ts';
import {
	getMutationResultError,
	syncMutationCacheBridgeSafely,
	type MutationCacheBridgeSource,
} from './cacheBridge/mutationCacheBridgeSync.ts';
import {
	syncApolloRefetchQueriesFromCache,
	syncRelayRefetchQueryPromise,
} from './cacheBridge/mutation/syncRefetchQueryResult.ts';
import {
	syncMutationCacheBridgeAfterOptimisticRollback,
	syncOptimisticMutationCacheBridge,
} from './cacheBridge/mutation/optimisticMutationPayloadSync.ts';
import { runWithAmbientStoreProxy } from './optimisticInterop/ambientStoreProxy.ts';

export type MutateInteropSupportedResult<TData> = Pick<FetchResult<TData>, 'data' | 'errors'>;

/**
 * Shape of a single `refetchQueries` entry accepted by `mutateInterop`.
 *
 * Mirrors the shape accepted by `useMutationInterop`: `query` is a Relay
 * `GraphQLTaggedNode` (not Apollo's `DocumentNode`). The Apollo build variant
 * converts each `query` via `toDocumentNode` before forwarding to Apollo's
 * `client.mutate`. The Relay variant uses the tagged node directly with
 * `fetchQuery`.
 */
export type MutateInteropRefetchQueryEntry = {
	query: GraphQLTaggedNode;
	// `Record<string, unknown>` rather than `any` so wrong variable shapes are
	// not silently accepted. Variable types are not known statically at this
	// type — Relay carries the per-query `$variables` on its generated artifact
	// (e.g. `MyQuery$variables`), and callers should cast / build the object to
	// satisfy the specific query's required variables. `unknown` keeps the
	// boundary loose enough to accept any shape while signalling that values
	// are not type-checked here.
	variables?: Record<string, unknown>;
};

// Subset of fields from Apollo's MutationOptions that are supported by mutateInterop.
// Note: on Relay, the `refetchQueries` function-form callback and the `update` cache-writer
// callback only receive `data` / `errors` — `context` / `extensions` are undefined, and the
// `DataProxy` argument to `update` is passed as `null`. Apollo passes the full shapes natively.
//
// `refetchQueries` is overridden (not picked from `MutationOptions`) because Apollo's
// `RefetchQueryDescription` types `query` as `DocumentNode`, but interop callers pass
// Relay `GraphQLTaggedNode`s. See `MutateInteropRefetchQueryEntry`.
export type MutateInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	MutationOptions<TData, TVariables>,
	| 'variables'
	| 'errorPolicy'
	| 'fetchPolicy'
	| 'optimisticResponse'
	| 'update'
	| 'awaitRefetchQueries'
> & {
	mutation: GraphQLTaggedNode;
	refetchQueries?:
		| Array<MutateInteropRefetchQueryEntry>
		| ((result: MutateInteropSupportedResult<TData>) => Array<MutateInteropRefetchQueryEntry>);
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function mutateInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: MutateInteropSupportedArgs<TData, TVariables>,
): Promise<MutateInteropSupportedResult<TData>> {
	const source: MutationCacheBridgeSource = isRelayEnabled(params.__callerPackage__, {
		phase: 'mutation',
	})
		? 'relay'
		: 'apollo';
	const optimisticData =
		typeof params.optimisticResponse === 'function' ? undefined : params.optimisticResponse;
	const optimisticRollback =
		optimisticData == null
			? null
			: syncOptimisticMutationCacheBridge({
					source,
					mutation: params.mutation,
					variables: params.variables,
					data: optimisticData,
					fetchPolicy: params.fetchPolicy,
				});

	let originalMutationPromise: Promise<MutateInteropSupportedResult<TData>>;
	try {
		originalMutationPromise =
			source === 'relay'
				? mutateInteropRelay<TData, TVariables>(params)
				: mutateInteropApollo<TData, TVariables>(params);
	} catch (error) {
		optimisticRollback?.rollback();
		throw error;
	}

	return originalMutationPromise.then(
		(result) => {
			// Mutation cache sync is deliberately post-success secondary work. If the
			// original mutation rejects, this callback never runs and the caller sees
			// the same rejection as before the cache bridge existed.
			syncMutationCacheBridgeAfterOptimisticRollback(optimisticRollback, () =>
				syncMutationCacheBridgeSafely({
					source,
					mutation: params.mutation,
					variables: params.variables,
					data: result.data,
					fetchPolicy: params.fetchPolicy,
					error: getMutationResultError(result),
				}),
			);

			return result;
		},
		(error) => {
			optimisticRollback?.rollback();
			throw error;
		},
	);
}

function mutateInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: MutateInteropSupportedArgs<TData, TVariables>,
): Promise<MutateInteropSupportedResult<TData>> {
	const { refetchQueries, awaitRefetchQueries, optimisticResponse, update } = params;

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const env = window.GLOBAL_RELAY_ENV!;
	const operationVariables = params.variables ?? ({} as TVariables);

	// Apollo parity via Relay's native optimistic layer: run the caller's `update` inside
	// commitMutation's `optimisticUpdater` with its interop writes routed onto the provided store
	// proxy (see runWithAmbientStoreProxy), so Relay reverts the whole optimistic layer atomically
	// on error — no manual snapshot/replay. After the response passes error-policy validation,
	// commitUpdate runs the caller update with server data. Mirrors useMutationInterop.
	let resolvedOptimistic: TData | undefined;
	try {
		resolvedOptimistic =
			typeof optimisticResponse === 'function'
				? (optimisticResponse as (variables: TVariables) => TData)(operationVariables)
				: (optimisticResponse as TData | undefined);
	} catch (err) {
		// Preserve the prior contract: a throwing function-form optimisticResponse rejects the
		// returned promise rather than throwing synchronously.
		return Promise.reject(err);
	}

	return apolloCompatibleMutation<TData>({
		fetchData: () =>
			commitMutation(env, {
				mutation: params.mutation,
				variables: operationVariables,
				optimisticUpdater:
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					resolvedOptimistic != null && (update || fg('confluence_relay_parity_fixes'))
						? (store) =>
								runWithAmbientStoreProxy(store, () => {
									// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
									if (fg('confluence_relay_parity_fixes')) {
										writeQueryPayloadToStore(
											store,
											getRequest(params.mutation).operation
												.selections as readonly ReaderSelection[],
											resolvedOptimistic as Record<string, unknown>,
											operationVariables,
										);
									}
									update?.(null as never, { data: resolvedOptimistic } as FetchResult<TData>);
								})
						: undefined,
				updater:
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					update && !fg('confluence_relay_parity_fixes')
						? (store, data) =>
								runWithAmbientStoreProxy(store, () =>
									update(
										null as never,
										{
											data: data as unknown as TData,
										} as FetchResult<TData>,
									),
								)
						: undefined,
			}) as Promise<TData>,
		errorPolicy: params.errorPolicy,
		convertToApolloError,
	}).then((mutationResult) => {
		// Match Apollo: failed mutations with errorPolicy 'none' never run derived cache writes.
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (update && mutationResult.data && fg('confluence_relay_parity_fixes')) {
			env.commitUpdate((store) =>
				runWithAmbientStoreProxy(store, () =>
					update(null as never, {
						data: mutationResult.data,
						...(mutationResult.errors?.length ? { errors: mutationResult.errors } : {}),
					}),
				),
			);
		}

		// `== null` covers both `undefined` (per the Apollo type) and `null` (callers using `as any`).
		if (refetchQueries == null) {
			return mutationResult;
		}

		// Refetches run whenever `apolloCompatibleMutation` returned a result (including
		// `errorPolicy: 'all' | 'ignore'` recoveries) — matches Apollo, which still runs
		// refetchQueries with partial data/errors. With the default `errorPolicy: 'none'`, a
		// mutation error would have thrown, so this `.then` never executes.

		// See the note on `MutateInteropSupportedArgs` — only `data` / `errors` are exposed here.
		const resolvedEntries =
			typeof refetchQueries === 'function'
				? refetchQueries({
						data: mutationResult.data,
						errors: mutationResult.errors,
					} as FetchResult<TData>)
				: refetchQueries;

		const refetchPromises = (
			resolvedEntries as unknown as Array<{ query?: GraphQLTaggedNode; variables?: any }>
		).map((entry) => {
			// `GraphQLTaggedNode` covers both `ConcreteRequest` (kind: 'Request') and `ReaderFragment`
			// (kind: 'Fragment'); only the former is valid for `fetchQuery`.
			if (!entry || !entry.query || (entry.query as any).kind !== 'Request') {
				throw new Error(
					`[Relay migration] refetchQueries entries must be \`{ query: graphql\`query ...\`, variables? }\` (a query, not a fragment). Received: ${JSON.stringify(entry)}`,
				);
			}
			const refetchVariables = entry.variables ?? {};
			return syncRelayRefetchQueryPromise(
				fetchQuery(env, entry.query, refetchVariables, {
					fetchPolicy: 'network-only',
				}).toPromise(),
				{
					query: entry.query,
					variables: refetchVariables,
				},
			);
		});

		// Refetch errors always reject (with `awaitRefetchQueries`) — `errorPolicy` only covers the mutation.
		if (awaitRefetchQueries && refetchPromises.length > 0) {
			return Promise.all(refetchPromises).then(() => mutationResult);
		}
		// Apollo Client v2 effectively drops fire-and-forget refetch promises (`Promise.all([])` —
		// see QueryManager.mutate). We attach a per-promise catch so every failure is observable.
		refetchPromises.forEach((p) => {
			p.catch((refetchError) => {
				// eslint-disable-next-line no-console
				console.warn('[Relay migration] refetchQueries error:', refetchError);
			});
		});
		return mutationResult;
	});
}

function mutateInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>({
	mutation,
	refetchQueries,
	...rest
}: MutateInteropSupportedArgs<TData, TVariables>): Promise<MutateInteropSupportedResult<TData>> {
	let refetchEntriesForSync: readonly unknown[] | undefined;

	// `useMutationInterop` already does this conversion for the hook variant; mirror it here so
	// `mutateInterop` accepts Relay `GraphQLTaggedNode`s in both build variants. Apollo's
	// `client.mutate` requires `PureQueryOptions` (`query: DocumentNode`), so we map each entry
	// through `toDocumentNode`. Capture the original Relay entries at the same time so successful
	// Apollo refetch results can be mirrored into Relay after Apollo writes them to its own cache.
	const convertEntries = (entries: Array<MutateInteropRefetchQueryEntry>) => {
		refetchEntriesForSync = entries;

		return entries.map((entry) => {
			return {
				query: toDocumentNode(entry.query),
				variables: entry.variables,
			};
		});
	};

	let apolloRefetchQueries: MutationOptions<TData, TVariables>['refetchQueries'];
	if (typeof refetchQueries === 'function') {
		const fn = refetchQueries;
		apolloRefetchQueries = (result: FetchResult<TData>) =>
			convertEntries(fn(result) as Array<MutateInteropRefetchQueryEntry>);
	} else if (refetchQueries) {
		apolloRefetchQueries = convertEntries(refetchQueries as Array<MutateInteropRefetchQueryEntry>);
	}

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const apolloClient = window['GLOBAL_APOLLO_CLIENT']!;
	return apolloClient
		.mutate({
			...rest,
			mutation: toDocumentNode(mutation),
			refetchQueries: apolloRefetchQueries,
		})
		.then((result) => {
			syncApolloRefetchQueriesFromCache({
				entries: refetchEntriesForSync,
				awaitRefetchQueries: rest.awaitRefetchQueries,
			});

			return result;
		});
}
